// Notification sound service for live chat alerts
class NotificationSoundService {
  constructor() {
    this.audioContext = null;
    this.enabled = true;
    this.volume = 0.8; // Increased default volume for better audibility
    this.initialized = false;
    this.lastPlayTime = 0;
    this.minPlayInterval = 500; // Minimum ms between sounds to prevent spam
    
    // Load saved preferences
    const savedEnabled = localStorage.getItem('chatNotificationSound');
    const savedVolume = localStorage.getItem('chatNotificationVolume');
    if (savedEnabled !== null) this.enabled = savedEnabled === 'true';
    if (savedVolume !== null) this.volume = parseFloat(savedVolume);
    
    console.log('[NotificationSound] Service created, enabled:', this.enabled, 'volume:', this.volume);
    
    // Auto-initialize on any user interaction for better reliability
    this.setupAutoInit();
  }
  
  // Setup auto-initialization on first user interaction
  setupAutoInit() {
    const initOnInteraction = async () => {
      if (!this.initialized) {
        console.log('[NotificationSound] Auto-initializing on user interaction');
        await this.init();
      }
    };
    
    // Listen for various user interactions
    ['click', 'keydown', 'touchstart', 'scroll'].forEach(event => {
      document.addEventListener(event, initOnInteraction, { once: true, passive: true });
    });
  }

  // Initialize audio context (must be called after user interaction)
  async init() {
    if (!this.audioContext) {
      try {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        console.log('[NotificationSound] AudioContext created, state:', this.audioContext.state);
      } catch (e) {
        console.error('[NotificationSound] Failed to create AudioContext:', e);
        return null;
      }
    }
    
    // Resume if suspended
    if (this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
        console.log('[NotificationSound] AudioContext resumed from suspended state');
      } catch (e) {
        console.error('[NotificationSound] Failed to resume AudioContext:', e);
      }
    }
    
    this.initialized = true;
    return this.audioContext;
  }
  
  // Force resume the audio context (useful when tab becomes active)
  async forceResume() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
        console.log('[NotificationSound] Force resumed AudioContext');
        return true;
      } catch (e) {
        console.error('[NotificationSound] Force resume failed:', e);
        return false;
      }
    }
    return true;
  }

  // Play a chime sound for new chat notifications
  async playChime() {
    console.log('[NotificationSound] playChime called, enabled:', this.enabled, 'initialized:', this.initialized);
    
    if (!this.enabled) {
      console.log('[NotificationSound] Sound is disabled, skipping');
      return false;
    }
    
    // Throttle to prevent sound spam
    const now = Date.now();
    if (now - this.lastPlayTime < this.minPlayInterval) {
      console.log('[NotificationSound] Throttled - too soon since last play');
      return false;
    }
    this.lastPlayTime = now;
    
    try {
      // Ensure context is initialized and resumed
      await this.forceResume();
      const ctx = await this.init();
      if (!ctx) {
        console.error('[NotificationSound] No AudioContext available');
        return false;
      }
      
      // Check if context is running
      if (ctx.state !== 'running') {
        console.warn('[NotificationSound] AudioContext not running, state:', ctx.state);
        await ctx.resume();
      }
      
      console.log('[NotificationSound] Playing chime, context state:', ctx.state);

      const currentTime = ctx.currentTime;
      
      // Create a pleasant two-tone chime (louder and more noticeable)
      const frequencies = [523.25, 659.25, 783.99]; // C5, E5, G5 chord
      
      frequencies.forEach((freq, index) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(freq, currentTime);
        
        // Stagger the notes slightly for a pleasant effect
        const startTime = currentTime + (index * 0.05);
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(this.volume * 0.6, startTime + 0.05);
        gainNode.gain.linearRampToValueAtTime(0, startTime + 0.5);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + 0.6);
      });
      
      // Play a second chime after a short delay for emphasis
      setTimeout(async () => {
        if (!this.enabled) return;
        try {
          const ctx2 = await this.init();
          if (!ctx2 || ctx2.state !== 'running') return;
          
          frequencies.forEach((freq, index) => {
            const oscillator = ctx2.createOscillator();
            const gainNode = ctx2.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(ctx2.destination);
            
            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(freq * 1.2, ctx2.currentTime); // Higher pitch
            
            const startTime = ctx2.currentTime + (index * 0.05);
            gainNode.gain.setValueAtTime(0, startTime);
            gainNode.gain.linearRampToValueAtTime(this.volume * 0.5, startTime + 0.05);
            gainNode.gain.linearRampToValueAtTime(0, startTime + 0.4);
            
            oscillator.start(startTime);
            oscillator.stop(startTime + 0.5);
          });
        } catch (e) {
          // Ignore errors in delayed sound
        }
      }, 300);
      
      console.log('[NotificationSound] Chime played successfully');
      return true;
      
    } catch (error) {
      console.error('[NotificationSound] Could not play chime:', error);
      return false;
    }
  }

  // Play an urgent alert sound (for high priority)
  async playUrgentAlert() {
    console.log('[NotificationSound] playUrgentAlert called, enabled:', this.enabled);
    
    if (!this.enabled) return;
    
    try {
      const ctx = await this.init();
      if (!ctx) return;

      const now = ctx.currentTime;
      
      // Create an attention-grabbing two-tone alert
      for (let i = 0; i < 3; i++) {
        const startTime = now + (i * 0.4);
        
        // High tone
        const osc1 = ctx.createOscillator();
        const gain1 = ctx.createGain();
        osc1.connect(gain1);
        gain1.connect(ctx.destination);
        osc1.type = 'sine';
        osc1.frequency.setValueAtTime(880, startTime); // A5
        gain1.gain.setValueAtTime(0, startTime);
        gain1.gain.linearRampToValueAtTime(this.volume * 0.6, startTime + 0.05);
        gain1.gain.linearRampToValueAtTime(0, startTime + 0.15);
        osc1.start(startTime);
        osc1.stop(startTime + 0.2);
        
        // Low tone
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(659.25, startTime + 0.15); // E5
        gain2.gain.setValueAtTime(0, startTime + 0.15);
        gain2.gain.linearRampToValueAtTime(this.volume * 0.6, startTime + 0.2);
        gain2.gain.linearRampToValueAtTime(0, startTime + 0.35);
        osc2.start(startTime + 0.15);
        osc2.stop(startTime + 0.4);
      }
      
      console.log('[NotificationSound] Urgent alert played');
      
    } catch (error) {
      console.error('[NotificationSound] Could not play urgent alert:', error);
    }
  }

  // Toggle sound on/off
  async toggle() {
    this.enabled = !this.enabled;
    localStorage.setItem('chatNotificationSound', this.enabled.toString());
    console.log('[NotificationSound] Sound toggled:', this.enabled);
    
    // Play test sound when enabling
    if (this.enabled) {
      await this.playChime();
    }
    
    return this.enabled;
  }

  // Set volume (0-1)
  setVolume(vol) {
    this.volume = Math.max(0, Math.min(1, vol));
    localStorage.setItem('chatNotificationVolume', this.volume.toString());
  }

  // Check if enabled
  isEnabled() {
    return this.enabled;
  }
}

// Singleton instance
export const notificationSound = new NotificationSoundService();
